import React, { useState } from 'react'
import { dsnApiV2 } from 'services/axios'

import Button from '@react/react-spectrum/Button'
import FieldLabel from '@react/react-spectrum/FieldLabel'
import Label from '@react/react-spectrum/Label'
import Select from '@react/react-spectrum/Select'
import { Table, THead, TH, TBody, TR, TD } from '@react/react-spectrum/Table'
import Textfield from '@react/react-spectrum/Textfield'
import { error } from '@react/react-spectrum/Toast/js/ToastContainer'
import Wait from '@react/react-spectrum/Wait'

import { getNitrogenUrl } from 'services/environment'
import { getUserId } from 'utils/user'

const CHANNEL_OPTIONS = [
  { label: 'Web', value: 'web' },
  { label: 'Mobile', value: 'mobile' },
  { label: 'Any', value: undefined },
]

const PROJECT_CHANNELS = {
  mobile: 'mobile',
  'web-modular': 'web',
  'customer-experience': 'cx-app',
}

export function LaunchTab({ environments, launch, aep, imsOrg, channel, projectName, projectType, onChange }) {
  const { imsOrgs = [], sandboxes = [], launchProperties = [] } = environments

  const [validatedPropertyId, setValidatedPropertyId] = useState()
  const [_org, setOrg] = useState(imsOrg?.id)
  const [_sandbox, setSandbox] = useState(aep?.sandbox || '')
  const [_propertyId, setPropertyId] = useState(launch?.propertyID)
  const [_channel, setChannel] = useState(channel)
  const [_searchPhrase, setSearchPhrase] = useState(getUserId()?.split('@')?.[0])

  const selectLaunch = (id) => {
    const property = launchProperties.find((p) => p.propertyID === id)
    const imsOrg = imsOrgs.find((o) => o.id === property.imsOrgID) || { id: _org }
    const sandbox = sandboxes.find((s) => s.id === property.aepID) || { sandboxName: property?.sandboxName || _sandbox }

    setValidatedPropertyId(id)
    fetchLaunchDetails(id)
      .then(
        (details) => {
          const launch = {
            propertyId: details?.propertyID,
            propertyID: details?.propertyID,
            appId: property?.appID,
            appID: property?.appID,
            ...property,
            propertyName: details?.name || property?.propertyName,
            companyId: details?.companyId,
          }
          const target = details.solutions?.target

          // include imsOrg in aep for backward compatibility
          let aep = sandbox && { ...sandbox, sandbox: sandbox?.sandboxName, imsOrg }

          setPropertyId(id)
          onChange((i) => ({ ...i, imsOrg, aep, launch, target }))
        },
        () => error('Could not validate selected launch property. Check if property exists.')
      )
      .finally(() => setValidatedPropertyId(null))
  }

  const filteredProperties = launchProperties
    .filter(({ propertyID, propertyName }) => {
      if (_searchPhrase) {
        return _propertyId === propertyID || propertyName?.toLowerCase()?.includes(_searchPhrase?.toLowerCase())
      }
      return true
    })
    .filter(({ propertyID, type }) => {
      if (_channel) {
        return _propertyId === propertyID || type === _channel
      }
      return true
    })

  const onOrgChange = (orgId) => {
    setOrg(orgId)
    setSandbox(null)
  }

  return (
    <div className='AutomaticTab'>
      <header>
        <ImsOrgPicker imsOrgs={imsOrgs} selectedOrgId={_org} onChange={onOrgChange} />
        <SandboxPicker sandboxes={sandboxes} selectedSandboxName={_sandbox} imsOrgId={_org} onChange={setSandbox} />
        <FieldLabel label='Channel type'>
          <Select options={CHANNEL_OPTIONS} value={_channel} onChange={setChannel} style={{ width: '100px' }} />
        </FieldLabel>
        <FieldLabel label='Search'>
          <Textfield value={_searchPhrase} onChange={setSearchPhrase} style={{ width: '150px' }} />
        </FieldLabel>
      </header>
      <div className='ImsOrgDialog__launch'>
        <LaunchTable
          imsOrgs={imsOrgs}
          launchProperties={filteredProperties}
          selectedOrgId={_org}
          selectedSandboxName={_sandbox}
          selectedPropertyId={_propertyId}
          validatedPropertyId={validatedPropertyId}
          projectName={projectName}
          projectType={projectType}
          onSelect={selectLaunch}
        />
      </div>
    </div>
  )
}
function LaunchTable({
  launchProperties,
  selectedPropertyId,
  imsOrgs,
  selectedOrgId,
  selectedSandboxName,
  validatedPropertyId,
  projectName,
  projectType,
  onSelect,
}) {
  const mappedProperties = launchProperties
    .filter((p) => !!p.appID)
    .map((p) => {
      const propertyOrg = imsOrgs.find((o) => o.id === p.imsOrgID)
      const sandboxName = p.sandboxName || p.aep?.sandboxName
      const propertySandboxUrl =
        sandboxName &&
        propertyOrg?.tenantID &&
        `https://experience.adobe.com/#/@${propertyOrg.tenantID}/sname:${sandboxName}/platform/home`

      return {
        ...p,
        imsOrgName: propertyOrg?.name,
        sandboxName,
        sandboxUrl: propertySandboxUrl,
      }
    })

  const filteredProperties = mappedProperties
    .filter(({ imsOrgID }) => (selectedOrgId ? selectedOrgId === imsOrgID : true))
    .filter(({ sandboxName }) => (selectedSandboxName ? selectedSandboxName === sandboxName : true))
    .sort((a, b) => (b.createdAt > a.createdAt ? 1 : -1))

  const hasProperties = filteredProperties?.length > 0

  if (!hasProperties) {
    return (
      <div className='LaunchTable__emptyState'>
        <em>
          No Tag Properties found. Please review filter criteria or proceed to <a href={getNitrogenUrl()}>Dashboard</a>{' '}
          to configure new properties.
        </em>
      </div>
    )
  }

  const isSuggested = (p) => {
    const type = p.propertyName?.match(/\([a-z-]*\)/)?.[0]?.slice(1, -1)
    if (type) {
      return PROJECT_CHANNELS[projectType]?.startsWith(type) && p.propertyName?.includes(projectName)
    } else {
      return p.propertyName?.includes(projectName)
    }
  }

  return (
    <Table className='LaunchTable'>
      <THead>
        <TH>Property Name</TH>
        <TH>IMS Org</TH>
        <TH>AEP Sandbox</TH>
        <TH style={{ width: '110px' }}>Created At</TH>
        <TH style={{ width: '232px' }}></TH>
      </THead>
      <TBody>
        {filteredProperties.map((p) => (
          <TR key={p.id} className={selectedPropertyId === p.propertyID ? 'is-selected' : ''}>
            <TD>
              {isSuggested(p) ? (
                <>
                  <Label variant='blue'>Suggested</Label>
                  &nbsp;&nbsp;{p.propertyName}
                </>
              ) : (
                p.propertyName
              )}
            </TD>
            <TD>{p.imsOrgName}</TD>
            <TD>{p.sandboxName || '---'}</TD>
            <TD>{formatDate(p.createdAt)}</TD>
            <TD>
              <Button variant='secondary' quiet onClick={() => p.launchUrl && window.open(p.launchUrl)}>
                View
              </Button>
              <SelectPropertyButton
                propertyId={p.propertyID}
                selectedPropertyId={selectedPropertyId}
                validatedPropertyId={validatedPropertyId}
                onSelect={onSelect}
              />
            </TD>
          </TR>
        ))}
      </TBody>
    </Table>
  )
}

function ImsOrgPicker({ imsOrgs, selectedOrgId, onChange }) {
  const imsOrgOptions = imsOrgs
    .map((org) => ({ label: org.name, value: org.id }))
    .concat({ label: 'Any organization', value: '' })
  return (
    <FieldLabel label='IMS Organization *'>
      <Select options={imsOrgOptions} value={selectedOrgId} onChange={onChange} />
    </FieldLabel>
  )
}

function SandboxPicker({ sandboxes, selectedSandboxName, imsOrgId, onChange }) {
  const sandboxOptions = sandboxes
    .filter((s, idx, arr) => s.imsOrgID === imsOrgId && arr.findIndex((a) => a.sandboxName === s.sandboxName) === idx)
    .map((s) => ({ label: s.sandboxName, value: s.sandboxName }))
    .concat({ label: 'Any sandbox', value: '' })

  return (
    <FieldLabel label='AEP Sandbox'>
      <Select
        options={sandboxOptions}
        value={selectedSandboxName || ''}
        onChange={onChange}
        style={{ width: '200px' }}
      />
    </FieldLabel>
  )
}

function SelectPropertyButton({ propertyId, selectedPropertyId, validatedPropertyId, onSelect }) {
  if (propertyId === validatedPropertyId) {
    return (
      <Button variant='cta' disabled={true}>
        <Wait size='S' />
        &nbsp;Validating
      </Button>
    )
  }
  if (propertyId === selectedPropertyId) {
    return (
      <Button variant='cta' disabled={true}>
        Selected
      </Button>
    )
  }
  return (
    <Button variant='cta' onClick={() => onSelect(propertyId)}>
      Select
    </Button>
  )
}

async function fetchLaunchDetails(propertyId) {
  const response = await dsnApiV2.get('/launch', { params: { propertyId } })
  const property = response?.data

  return {
    companyId: property?.relationships?.company?.data?.id,
    propertyName: property?.attributes?.name,
    solutions: property.solutions,
    profileViewer: {
      extensionId: property?.profileViewer?.id,
      projectId: property?.profileViewer?.projectId,
      // rule: false,
    },
  }
}

function formatDate(timestamp) {
  try {
    return Intl.DateTimeFormat(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(new Date(timestamp))
  } catch (e) {
    return ''
  }
}
